import ServiceLayout from '@/pages/services/components/ServiceLayout';
import type { ServiceContent } from '@/pages/services/components/ServiceLayout';

export const content: ServiceContent = {
  tag: 'Development',
  title: 'システムを作っても、業務が変わらなければ意味がない。',
  subtitle: 'システムを作ることが目的ではありません。',
  description:
    '業務の流れを確認し、必要な機能と運用方法を考えたうえで、業務に合うAIシステムを設計します。',
  icon: 'ri-tools-line',
  heroColor: {
    blob1: 'linear-gradient(135deg, #EA580C, #D97706)',
    blob2: 'linear-gradient(135deg, #F59E0B, #DC2626)',
    accent: '#FB923C',
    glow: 'from-orange-400 to-amber-400',
  },
  aboutTitle: 'AIシステム開発とは',
  aboutParagraphs: [
    '企業ごとの業務や課題に合わせて、AIを組み込んだシステムを設計・開発するサービスです。最初から大規模なシステムを作ることを前提にしません。',
    'どの業務にどのような課題があるのかを確認します。既存ツールで対応できるかを検討します。必要なシステムを設計します。開発後の運用や改善が必要かどうかも確認します。',
  ],
  challenges: [
    '既存のシステムでは、自社の業務に対応しきれない。',
    '複数のツールを使い分けることで、業務が複雑になっている。',
    'AIを組み込んだ仕組みを作りたいが、必要な機能が分からない。',
    'システムを導入しても、現場が使い続けられるか不安がある。',
    '開発後の運用や改善まで考えられていない。',
    '業務に合わせたシステムを作りたいが、相談先が分からない。',
  ],
  featuresTitle: 'AIシステム開発でできること',
  features: [
    {
      icon: 'ri-draft-line',
      title: '業務に合わせたシステム設計',
      items: ['既存の業務フローを確認し、必要な機能を設計します。'],
    },
    {
      icon: 'ri-database-2-line',
      title: '情報を活用する仕組みづくり',
      items: ['分散した情報を集約し、活用できる状態に整えます。'],
    },
    {
      icon: 'ri-robot-line',
      title: 'AIを組み込んだ業務支援',
      items: ['AIによる判断・作成・検索の支援を組み込みます。'],
    },
    {
      icon: 'ri-links-line',
      title: '既存業務との接続',
      items: ['既存のツールや業務とつなげて運用します。'],
    },
    {
      icon: 'ri-settings-5-line',
      title: '運用を前提にした開発',
      items: ['開発後の運用・改善まで見据えて設計します。'],
    },
  ],
  valueTitle: '導入後に得られる価値',
  value: [
    '複数のツールを行き来する時間を見直せる。',
    '必要な情報を確認しやすくなる。',
    '社内の知識や情報を活用しやすくなる。',
    '担当者に依存していた業務を共有しやすくなる。',
    '業務に合わせた機能を使いやすくなる。',
    '経営者が、システムの確認ではなく事業の判断に時間を使いやすくなる。',
    '業務の変化に合わせて、次の改善を検討しやすくなる。',
  ],
  whyTitle: 'なぜLuviraなのか',
  why: [
    'LUVIRAは、システムを作ることだけを目的にしません。開発前に業務と課題を確認します。本当にシステム化が必要かを検討します。既存ツールで対応できる方法も含めて考えます。現場で使われる業務フローを設計します。AIを使う部分と人が判断する部分を分けます。開発後の運用・改善が必要かを確認します。必要に応じてAI業務改善やAI運用・改善支援へつなげます。',
  ],
  casesTitle: '開発事例',
  cases: [
    {
      icon: 'ri-mail-send-line',
      title: 'AI自動リスト獲得・メール営業ツール',
      description: '営業対象となる企業情報の収集から、メールによるアプローチまでを支援する社内ツールを開発。',
    },
    {
      icon: 'ri-film-line',
      title: 'AI動画解析ツール',
      description: '動画データをAIで解析し、内容の確認や情報抽出を支援する社内ツールを開発。',
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'AI Meta広告分析ツール',
      description: 'Meta広告の配信データとLPを分析し、広告運用における強み・弱みの把握から改善案の提案まで行うSaaSツールを開発。',
    },
    {
      icon: 'ri-robot-2-line',
      title: 'AI自律開発ツール',
      description: 'AIが24時間自律的に開発作業を進める、社内向けのAI開発ツールを開発。',
    },
    {
      icon: 'ri-mic-line',
      title: 'AI議事録Bot',
      description: '会議内容を記録・整理し、議事録作成を支援するAI Botを開発。',
    },
  ],
  flowTitle: '開発までの流れ',
  flow: [
    { step: 'STEP 1', title: '業務と課題を確認する', items: ['現在の業務内容と課題を把握します。'] },
    { step: 'STEP 2', title: 'システム化する範囲を決める', items: ['システム化する業務と範囲を定めます。'] },
    { step: 'STEP 3', title: 'システムを設計する', items: ['必要な機能と業務フローを設計します。'] },
    { step: 'STEP 4', title: '開発して使い始める', items: ['システムを開発し、現場で使い始めます。'] },
    { step: 'STEP 5', title: '運用と改善を検討する', items: ['利用状況を確認し、次の改善を検討します。'] },
  ],
  faq: [
    {
      q: 'AIに詳しくなくても相談できますか？',
      a: 'はい。AIやシステム開発の知識は必要ありません。現在の業務内容と困っていることを確認し、システム化できる業務や必要な進め方を一緒に考えます。',
    },
    {
      q: 'どのようなシステムを作れますか？',
      a: '情報検索、文書作成、問い合わせ対応、集計、報告、社内ナレッジ活用など、業務内容に応じて対応可能な範囲を検討します。',
    },
    {
      q: '既存のシステムやツールと連携できますか？',
      a: '既存システムやツールの仕様、データ、業務環境を確認したうえで、連携できるかどうかを検討します。',
    },
    {
      q: '最初から大規模な開発が必要ですか？',
      a: 'いいえ。まずは業務課題と必要な範囲を確認します。小さく始められる場合は、最初に取り組む範囲を限定して進めます。',
    },
    {
      q: '開発後もサポートしてもらえますか？',
      a: '開発後の運用や改善が必要な場合は、対応範囲と進め方を確認したうえで支援を検討します。',
    },
    {
      q: 'AIが出した結果をそのまま使うのが不安です。',
      a: 'AIが処理する範囲と、人が確認する範囲を分けて設計します。必要に応じて、確認や承認の流れも含めて検討します。',
    },
    {
      q: '相談したら、すぐに開発を始めなければなりませんか？',
      a: 'いいえ。初回は業務課題とシステム化の可能性を確認する場です。開発しない方がよい場合も含めて、現実的な進め方を検討します。',
    },
  ],
  cta: {
    title: '業務に合うAIシステムを、一緒に考える。',
    description:
      'AIシステムを作りたいものの、必要な機能や開発範囲が分からない。既存のツールで対応できるのか、新しく作る必要があるのか判断できない。その状態からご相談いただけます。LUVIRAが業務内容と課題を確認し、必要なシステムと進め方を一緒に考えます。',
    buttonText: 'AIシステム開発について相談する',
    serviceKey: 'AIシステム開発',
  },
};

export default function SystemDevelopmentPage() {
  return <ServiceLayout content={content} />;
}